import { isYandexMapsUrl } from './place';
import type {
  Place,
  PlaceStatus,
  ReturnIntent,
  StationIdCollection,
  UserData,
} from './types';

export const BACKUP_FORMAT_VERSION = 1 as const;

export interface BackupFile {
  version: typeof BACKUP_FORMAT_VERSION;
  createdAt: string;
  data: UserData;
}

export type BackupValidationResult =
  | { ok: true; value: BackupFile }
  | { ok: false; errors: string[] };

export class BackupValidationError extends Error {
  readonly errors: string[];

  constructor(errors: string[]) {
    super(errors[0] ?? 'Резервная копия имеет неверный формат.');
    this.name = 'BackupValidationError';
    this.errors = errors;
  }
}

const ROOT_KEYS = ['version', 'createdAt', 'data'] as const;
const DATA_KEYS = ['places', 'favoriteStationIds', 'customTags'] as const;
const PLACE_KEYS = [
  'id',
  'name',
  'yandexMapsUrl',
  'stationId',
  'status',
  'returnIntent',
  'tags',
  'comment',
  'createdAt',
  'updatedAt',
] as const;

const PLACE_STATUSES = new Set<PlaceStatus>(['want', 'visited']);
const RETURN_INTENTS = new Set<Exclude<ReturnIntent, null>>([
  'yes',
  'no',
  'unsure',
]);

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function validateExactKeys(
  object: Record<string, unknown>,
  expectedKeys: readonly string[],
  path: string,
  errors: string[],
): void {
  const expected = new Set(expectedKeys);

  for (const key of expectedKeys) {
    if (!Object.hasOwn(object, key)) {
      errors.push(`В поле «${path}» отсутствует обязательное свойство «${key}».`);
    }
  }

  for (const key of Object.keys(object)) {
    if (!expected.has(key)) {
      errors.push(`В поле «${path}» найдено неизвестное свойство «${key}».`);
    }
  }
}

function isIsoTimestamp(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[+-]\d{2}:\d{2})$/u.test(value) &&
    !Number.isNaN(Date.parse(value))
  );
}

function stationExists(
  stationIds: StationIdCollection | undefined,
  stationId: string,
): boolean {
  if (stationIds === undefined) {
    return true;
  }

  if (Array.isArray(stationIds)) {
    return stationIds.some((station) => station.id === stationId);
  }

  return (stationIds as ReadonlySet<string>).has(stationId);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function normaliseComparableText(value: string): string {
  return value
    .normalize('NFKC')
    .trim()
    .replace(/\s+/gu, ' ')
    .toLocaleLowerCase('ru-RU')
    .replaceAll('ё', 'е');
}

function validateUniqueStrings(
  value: unknown,
  path: string,
  errors: string[],
  options: { allowEmpty?: boolean } = {},
): value is string[] {
  if (!Array.isArray(value)) {
    errors.push(`Поле «${path}» должно быть массивом строк.`);
    return false;
  }

  const seen = new Set<string>();

  value.forEach((item, index) => {
    if (typeof item !== 'string' || (!options.allowEmpty && item.trim().length === 0)) {
      errors.push(`Элемент «${path}[${index}]» должен быть непустой строкой.`);
      return;
    }

    const key = normaliseComparableText(item);
    if (seen.has(key)) {
      errors.push(`Поле «${path}» содержит повторяющееся значение «${item}».`);
    }
    seen.add(key);
  });

  return true;
}

function validatePlace(
  value: unknown,
  index: number,
  stationIds: StationIdCollection | undefined,
  seenPlaceIds: Set<string>,
  errors: string[],
): value is Place {
  const path = `data.places[${index}]`;

  if (!isPlainObject(value)) {
    errors.push(`Поле «${path}» должно быть объектом места.`);
    return false;
  }

  validateExactKeys(value, PLACE_KEYS, path, errors);

  if (!isNonEmptyString(value.id)) {
    errors.push(`Поле «${path}.id» должно быть непустой строкой.`);
  } else if (seenPlaceIds.has(value.id)) {
    errors.push(`Идентификатор места «${value.id}» встречается несколько раз.`);
  } else {
    seenPlaceIds.add(value.id);
  }

  if (!isNonEmptyString(value.name)) {
    errors.push(`Поле «${path}.name» должно быть непустой строкой.`);
  }

  if (typeof value.yandexMapsUrl !== 'string' || !isYandexMapsUrl(value.yandexMapsUrl)) {
    errors.push(`Поле «${path}.yandexMapsUrl» должно содержать HTTPS-ссылку Яндекс Карт.`);
  }

  if (!isNonEmptyString(value.stationId)) {
    errors.push(`Поле «${path}.stationId» должно быть непустой строкой.`);
  } else if (!stationExists(stationIds, value.stationId)) {
    errors.push(`В поле «${path}.stationId» указана неизвестная станция.`);
  }

  if (!PLACE_STATUSES.has(value.status as PlaceStatus)) {
    errors.push(`Поле «${path}.status» содержит неизвестный статус.`);
  }

  if (
    value.returnIntent !== null &&
    !RETURN_INTENTS.has(value.returnIntent as Exclude<ReturnIntent, null>)
  ) {
    errors.push(`Поле «${path}.returnIntent» содержит неизвестное значение.`);
  }

  if (value.status === 'want' && value.returnIntent !== null) {
    errors.push(
      `Поле «${path}.returnIntent» должно быть null для места со статусом «want».`,
    );
  }

  validateUniqueStrings(value.tags, `${path}.tags`, errors);

  if (typeof value.comment !== 'string') {
    errors.push(`Поле «${path}.comment» должно быть строкой.`);
  }

  if (!isIsoTimestamp(value.createdAt)) {
    errors.push(`Поле «${path}.createdAt» должно содержать дату ISO 8601.`);
  }

  if (!isIsoTimestamp(value.updatedAt)) {
    errors.push(`Поле «${path}.updatedAt» должно содержать дату ISO 8601.`);
  }

  if (
    isIsoTimestamp(value.createdAt) &&
    isIsoTimestamp(value.updatedAt) &&
    Date.parse(value.updatedAt) < Date.parse(value.createdAt)
  ) {
    errors.push(`Дата изменения «${path}.updatedAt» не может быть раньше даты создания.`);
  }

  return true;
}

export function validateBackup(
  value: unknown,
  stationIds?: StationIdCollection,
): BackupValidationResult {
  const errors: string[] = [];

  if (!isPlainObject(value)) {
    return {
      ok: false,
      errors: ['Резервная копия должна содержать JSON-объект.'],
    };
  }

  validateExactKeys(value, ROOT_KEYS, 'корень файла', errors);

  if (value.version !== BACKUP_FORMAT_VERSION) {
    errors.push(
      `Неподдерживаемая версия резервной копии: ожидалась ${BACKUP_FORMAT_VERSION}.`,
    );
  }

  if (!isIsoTimestamp(value.createdAt)) {
    errors.push('Поле «createdAt» должно содержать дату создания в формате ISO 8601.');
  }

  if (!isPlainObject(value.data)) {
    errors.push('Поле «data» должно быть объектом с пользовательскими данными.');
  } else {
    validateExactKeys(value.data, DATA_KEYS, 'data', errors);

    if (!Array.isArray(value.data.places)) {
      errors.push('Поле «data.places» должно быть массивом мест.');
    } else {
      const seenPlaceIds = new Set<string>();
      value.data.places.forEach((place, index) => {
        validatePlace(place, index, stationIds, seenPlaceIds, errors);
      });
    }

    const favoriteStationIds = value.data.favoriteStationIds;
    const favoriteIdsAreStrings = validateUniqueStrings(
      favoriteStationIds,
      'data.favoriteStationIds',
      errors,
    );

    if (favoriteIdsAreStrings) {
      favoriteStationIds.forEach((stationId, index) => {
        if (!stationExists(stationIds, stationId)) {
          errors.push(
            `В поле «data.favoriteStationIds[${index}]» указана неизвестная станция.`,
          );
        }
      });
    }

    validateUniqueStrings(value.data.customTags, 'data.customTags', errors);
  }

  if (errors.length > 0) {
    return { ok: false, errors };
  }

  return { ok: true, value: value as unknown as BackupFile };
}

export function isValidBackup(
  value: unknown,
  stationIds?: StationIdCollection,
): value is BackupFile {
  return validateBackup(value, stationIds).ok;
}

export function createBackup(data: UserData, createdAt: string): BackupFile {
  const backup: BackupFile = {
    version: BACKUP_FORMAT_VERSION,
    createdAt,
    data: {
      places: data.places.map((place) => ({
        ...place,
        tags: [...place.tags],
      })),
      favoriteStationIds: [...data.favoriteStationIds],
      customTags: [...data.customTags],
    },
  };

  const result = validateBackup(backup);
  if (!result.ok) {
    throw new BackupValidationError(result.errors);
  }

  return backup;
}

export function serializeBackup(data: UserData, createdAt: string): string {
  return JSON.stringify(createBackup(data, createdAt), null, 2);
}

export function parseBackup(
  json: string,
  stationIds?: StationIdCollection,
): BackupFile {
  let parsed: unknown;

  try {
    parsed = JSON.parse(json) as unknown;
  } catch {
    throw new BackupValidationError([
      'Не удалось прочитать JSON-файл резервной копии.',
    ]);
  }

  const result = validateBackup(parsed, stationIds);
  if (!result.ok) {
    throw new BackupValidationError(result.errors);
  }

  return result.value;
}
